<template>
  <li class="agenda-item" :data-tone="schedule.tone">
    <div class="agenda-item__activity">
      <div class="agenda-item__header">
        <opportunity-activity-type-icon :type="activity.activity_type" />
        <p class="agenda-item__title">
          <span class="agenda-item__type">{{ typeLabel }}</span>
          <span class="agenda-item__separator">-</span>
          <span
            class="agenda-item__schedule"
            :class="`agenda-item__schedule--${schedule.tone}`"
          >
            {{ $t(schedule.key, schedule.params) }}
          </span>
        </p>
      </div>
      <p v-if="activity.description" class="agenda-item__description">
        {{ $t('OPPORTUNITY_DETAILS.ACTIVITIES.OBSERVATION') }}
        {{ activity.description }}
      </p>
      <p class="agenda-item__date">{{ scheduledFor }}</p>
    </div>

    <div class="agenda-item__opportunity">
      <p class="agenda-item__line">
        {{ $t('ACTIVITY_AGENDA.OPPORTUNITY') }}
        <router-link :to="opportunityPath" class="agenda-item__link">
          {{ opportunity.name }}
        </router-link>
      </p>
      <p class="agenda-item__line">
        {{ $t('ACTIVITY_AGENDA.STAGE') }}
        <span class="agenda-item__stage">{{ stageName }}</span>
      </p>
      <p class="agenda-item__line agenda-item__value">{{ formattedValue }}</p>
    </div>

    <div class="agenda-item__temperature">
      <p class="agenda-item__temperature-title">
        {{ $t('OPPORTUNITY_DETAILS.TEMPERATURE.TITLE') }}
      </p>
      <span class="agenda-item__temperature-box">
        <opportunity-temperature-icon
          :temperature="opportunity.temperature"
          :size="32"
        />
        <span class="agenda-item__temperature-label">
          {{ $t(temperatureLabel) }}
        </span>
      </span>
    </div>

    <div class="agenda-item__actions">
      <template v-if="canManage">
        <woot-button
          type="button"
          color-scheme="success"
          size="small"
          icon="checkmark-circle"
          class="agenda-item__complete"
          :is-disabled="isBusy"
          @click="$emit('complete', activity)"
        >
          {{ $t('OPPORTUNITY_DETAILS.ACTIVITIES.ACTIONS.COMPLETE') }}
        </woot-button>
        <woot-button
          type="button"
          color-scheme="secondary"
          size="small"
          icon="calendar-clock"
          class="agenda-item__icon-action agenda-item__reschedule"
          :title="$t('OPPORTUNITY_DETAILS.ACTIVITIES.ACTIONS.RESCHEDULE')"
          :aria-label="$t('OPPORTUNITY_DETAILS.ACTIVITIES.ACTIONS.RESCHEDULE')"
          :is-disabled="isBusy"
          @click="$emit('reschedule', activity)"
        />
        <woot-button
          type="button"
          color-scheme="secondary"
          size="small"
          icon="dismiss-circle"
          class="agenda-item__icon-action agenda-item__cancel"
          :title="$t('OPPORTUNITY_DETAILS.ACTIVITIES.ACTIONS.CANCEL')"
          :aria-label="$t('OPPORTUNITY_DETAILS.ACTIVITIES.ACTIONS.CANCEL')"
          :is-disabled="isBusy"
          @click="$emit('cancel', activity)"
        />
      </template>
      <p v-else-if="isLocked" class="agenda-item__locked">
        {{ $t('ACTIVITY_AGENDA.LOCKED') }}
      </p>
    </div>
  </li>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import { frontendURL } from 'dashboard/helper/URLHelper';
import {
  activityScheduleInfo,
  activityTypeLabelKey,
} from 'dashboard/helper/opportunityActivityHelper';
import { formatCurrencyValue } from 'dashboard/helper/opportunityDashboardHelper';
import { temperatureLabelKey } from 'dashboard/helper/opportunityTemperatureHelper';
import OpportunityActivityTypeIcon from '../activities/OpportunityActivityTypeIcon.vue';
import OpportunityTemperatureIcon from '../OpportunityTemperatureIcon.vue';

export default {
  name: 'ActivityAgendaItem',
  components: {
    OpportunityActivityTypeIcon,
    OpportunityTemperatureIcon,
  },
  props: {
    activity: {
      type: Object,
      required: true,
    },
    now: {
      type: Date,
      default: () => new Date(),
    },
    canWrite: {
      type: Boolean,
      default: false,
    },
    isBusy: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({ accountId: 'getCurrentAccountId' }),
    opportunity() {
      return this.activity.opportunity || {};
    },
    isLocked() {
      return !!this.opportunity.locked;
    },
    canManage() {
      return this.canWrite && !this.isLocked;
    },
    typeLabel() {
      return this.$t(activityTypeLabelKey(this.activity.activity_type));
    },
    schedule() {
      return activityScheduleInfo(this.activity, this.now);
    },
    scheduledFor() {
      const scheduledAt = new Date(this.activity.scheduled_at);
      return this.$t('ACTIVITY_AGENDA.SCHEDULED_FOR', {
        date: format(scheduledAt, 'dd/MM/yyyy'),
        time: format(scheduledAt, 'HH:mm'),
      });
    },
    opportunityPath() {
      return frontendURL(
        `accounts/${this.accountId}/opportunities/${this.opportunity.id}`
      );
    },
    stageName() {
      return (
        (this.opportunity.pipeline_stage &&
          this.opportunity.pipeline_stage.name) ||
        '-'
      );
    },
    formattedValue() {
      return formatCurrencyValue(
        this.opportunity.value,
        this.opportunity.currency || 'BRL'
      );
    },
    temperatureLabel() {
      return temperatureLabelKey(this.opportunity.temperature);
    },
  },
};
</script>

<style lang="scss" scoped>
.agenda-item {
  align-items: center;
  background: var(--white);
  border: 1px solid var(--s-50);
  border-radius: var(--border-radius-medium);
  box-shadow: var(--shadow-medium);
  display: grid;
  gap: var(--space-normal) var(--space-medium);
  grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr) 10rem 22rem;
  list-style: none;
  margin-bottom: var(--space-medium);
  padding: var(--space-normal) var(--space-medium);

  @media screen and (max-width: 1024px) {
    grid-template-columns: 1fr auto;
  }
}

.agenda-item__activity,
.agenda-item__opportunity {
  min-width: 0;
}

.agenda-item__opportunity {
  border-left: 1px solid var(--s-100);
  padding-left: var(--space-medium);

  @media screen and (max-width: 1024px) {
    border-left: 0;
    padding-left: 0;
  }
}

.agenda-item__header {
  align-items: center;
  display: flex;
  gap: var(--space-slab);
}

.agenda-item__title {
  color: var(--s-900);
  font-size: var(--font-size-default);
  margin: 0;
  min-width: 0;
}

.agenda-item__type,
.agenda-item__separator {
  font-weight: var(--font-weight-bold);
}

.agenda-item__schedule--overdue {
  color: var(--r-500);
  font-weight: var(--font-weight-bold);
}

.agenda-item__schedule--today {
  color: var(--g-700);
  font-weight: var(--font-weight-bold);
}

.agenda-item__schedule--future,
.agenda-item__schedule--neutral {
  color: var(--s-600);
}

.agenda-item__description {
  color: var(--s-800);
  font-size: var(--font-size-default);
  margin: var(--space-normal) 0 0;
  overflow-wrap: anywhere;
}

.agenda-item__date {
  color: var(--s-600);
  font-size: var(--font-size-small);
  margin: var(--space-smaller) 0 0;
}

.agenda-item__line {
  color: var(--s-900);
  font-size: var(--font-size-default);
  margin: 0 0 var(--space-small);
  overflow-wrap: anywhere;

  &:last-child {
    margin-bottom: 0;
  }
}

.agenda-item__link {
  color: var(--w-600);
  font-weight: var(--font-weight-medium);

  &:hover {
    color: var(--w-800);
    text-decoration: underline;
  }
}

.agenda-item__stage {
  font-weight: var(--font-weight-medium);
}

.agenda-item__value {
  font-weight: var(--font-weight-medium);
}

.agenda-item__temperature {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: var(--space-smaller);
}

.agenda-item__temperature-title {
  color: var(--s-900);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-bold);
  margin: 0;
}

.agenda-item__temperature-box {
  align-items: center;
  background: var(--white);
  border: 2px solid var(--c-500);
  border-radius: var(--border-radius-large);
  color: var(--b-900);
  display: inline-flex;
  flex-direction: column;
  gap: var(--space-micro);
  height: 7.2rem;
  justify-content: center;
  width: 7.2rem;
}

.agenda-item__temperature-label {
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-bold);
}

.agenda-item__actions {
  align-items: center;
  align-self: start;
  border-left: 1px solid var(--s-100);
  display: flex;
  gap: var(--space-small);
  justify-content: flex-end;
  min-height: 3.6rem;
  padding-left: var(--space-medium);

  @media screen and (max-width: 1024px) {
    border-left: 0;
    grid-column: 1 / -1;
    justify-content: flex-start;
    padding-left: 0;
  }
}

.agenda-item__actions .button {
  margin: 0;
}

.agenda-item__icon-action {
  justify-content: center;
  width: 3.2rem;
}

.agenda-item__locked {
  color: var(--s-600);
  font-size: var(--font-size-small);
  margin: 0;
}
</style>
